type EmscriptenFactory<TModule> = (opts?: {
  locateFile?: (path: string) => string;
}) => Promise<TModule>;

const factoryCache = new Map<string, Promise<unknown>>();
const scriptCache = new Map<string, Promise<void>>();

// Type-safe global lookup — no type assertions needed
const getGlobal = (name: string): unknown =>
  typeof globalThis !== "undefined" ? Reflect.get(globalThis, name) : undefined;

const readFactory = <TModule>(
  factoryName: string,
): EmscriptenFactory<TModule> | null => {
  const candidate = getGlobal(factoryName);
  return typeof candidate === "function"
    ? (candidate as EmscriptenFactory<TModule>)
    : null;
};

const loadScript = (scriptUrl: string): Promise<void> => {
  if (typeof document === "undefined") {
    return Promise.reject(
      new Error(`Cannot load Emscripten script without a DOM: ${scriptUrl}`),
    );
  }
  const existing = scriptCache.get(scriptUrl);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const existingTag = document.querySelector<HTMLScriptElement>(
      `script[data-emscripten-script="${scriptUrl}"]`,
    );
    if (existingTag) {
      if (existingTag.dataset["emscriptenLoaded"] === "true") {
        resolve();
        return;
      }

      existingTag.addEventListener(
        "load",
        () => {
          resolve();
        },
        { once: true },
      );
      existingTag.addEventListener(
        "error",
        () => {
          reject(new Error(`Failed to load Emscripten script: ${scriptUrl}`));
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.dataset["emscriptenScript"] = scriptUrl;
    script.addEventListener(
      "load",
      () => {
        script.dataset["emscriptenLoaded"] = "true";
        resolve();
      },
      { once: true },
    );
    script.addEventListener(
      "error",
      () => {
        reject(new Error(`Failed to load Emscripten script: ${scriptUrl}`));
      },
      { once: true },
    );
    document.head.appendChild(script);
  });

  scriptCache.set(scriptUrl, promise);
  return promise;
};

export const loadEmscriptenFactory = async <TModule>(
  factoryName: string,
  scriptUrl: string,
): Promise<EmscriptenFactory<TModule>> => {
  const cached = factoryCache.get(factoryName) as
    | Promise<EmscriptenFactory<TModule>>
    | undefined;
  if (cached) return cached;

  const promise = (async () => {
    const existingFactory = readFactory<TModule>(factoryName);
    if (existingFactory) return existingFactory;

    await loadScript(scriptUrl);

    const loadedFactory = readFactory<TModule>(factoryName);
    if (!loadedFactory) {
      throw new Error(
        `Emscripten factory ${factoryName} was not defined by ${scriptUrl}`,
      );
    }

    return loadedFactory;
  })();

  factoryCache.set(factoryName, promise);
  return promise;
};

export const resetEmscriptenLoaderForTest = (): void => {
  factoryCache.clear();
  scriptCache.clear();
};
